import { Form, Formik } from "formik";
import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import AddButton from "../../buttons/button";
import ApproveDeclineActions from "../approve-decline-actions";
import InputField from "../../forms/input-field";
import ImageField from "../../forms/images-field";
import CheckBox from "../../forms/check-box-field";
import { useDispatch } from "react-redux";
import { StoreAgreementCreateAction } from "../../../redux/actions/store-action";
import { api } from "../../../redux/apis/api";
import successToast from "../../helper-components/success-toast";
import errorToast from "../../helper-components/error-toast";
import PDFViewer from "../../helper-components/pdf-viewer";
import {
  buildDocumentParty,
  fetchStorePartners,
  firstFilled,
} from "../document-payload";

// A document column may hold a URL, a list of them or an upload object; this
// returns the first usable URL whichever shape the API sends.
const readDocumentUrl = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.startsWith("[")) {
      try {
        return readDocumentUrl(JSON.parse(trimmed));
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }
  if (Array.isArray(value)) return readDocumentUrl(value[0]);
  return readDocumentUrl(value.url ?? value.file_url ?? value.pdf_url ?? value.location);
};

const StoreAgreementTab = ({
  id,
  setStoreData,
  storeData,
  initialValue,
  disabled = false,
  permissionFn = () => {},
}) => {
  const dispatch = useDispatch();
  const [agreementPdfUrl, setAgreementPdfUrl] = useState(
    initialValue?.store_agreement_document ?? initialValue?.agreement_pdf_url ?? ""
  );
  // Operations manual PDF: saved on the store as `operational_manual`, and
  // otherwise generated with the agreement (`op_pdf_url`). Held in state like the
  // agreement URL, because a save replaces storeData.agreement with the save
  // response, which does not carry this field.
  const [operationalManualUrl, setOperationalManualUrl] = useState(() =>
    readDocumentUrl(initialValue?.operational_manual)
  );

  useEffect(() => {
    // Generating returns both PDFs, so it also runs for a store that has a saved
    // agreement but no manual yet; a saved agreement is never replaced.
    if (!agreementPdfUrl || !operationalManualUrl) {
      const buildPayload = (partners) => ({
        store_id: id,
        ...buildDocumentParty(storeData, initialValue),
        witness: firstFilled(initialValue?.agreement_witness),
        signature: firstFilled(storeData?.loi?.digital_sign, initialValue?.digital_sign),
        // Partners go as the partner API returns them.
        partner_details: partners,
      });

      fetchStorePartners(id)
        .then((partners) =>
          api.post("/cpos/app/getAgreementPdf", buildPayload(partners))
        )
        .then((res) => {
          const url = res.data?.data?.pdf_url ?? res.data?.data?.url ?? "";
          const manualUrl = readDocumentUrl(res.data?.data?.op_pdf_url);
          if (manualUrl) {
            setOperationalManualUrl((current) => current || manualUrl);
          }
          if (url) setAgreementPdfUrl((current) => current || url);
          else if (!agreementPdfUrl) errorToast("Failed to load agreement document");
        })
        .catch(() => errorToast("Failed to load agreement document"));
    }
  }, []);

  const initialValues = {
    agreement_witness: initialValue?.agreement_witness ?? "",
    store_agreement_remarks: initialValue?.store_agreement_remarks ?? "",
    store_agreement_digital_sign: initialValue?.store_agreement_digital_sign ?? "",
    store_agreement_with_davaindia_conditions:
      initialValue?.store_agreement_with_davaindia_conditions ?? false,
    store_agreement_is_approve: initialValue?.store_agreement_is_approve ?? false,
    store_agreement_is_reject: initialValue?.store_agreement_is_reject ?? false,
  };

  const validationSchema = Yup.object().shape({});
  const buildSavePayload = (values) => ({
    store_id: id ?? null,
    agreement_witness: values.agreement_witness,
    store_agreement_remarks: values.store_agreement_remarks,
    store_agreement_digital_sign: values.store_agreement_digital_sign,
    store_agreement_document: agreementPdfUrl,
    operational_manual: operationalManualUrl,
    store_agreement_with_davaindia_conditions:
      values.store_agreement_with_davaindia_conditions,
  });
  const onSubmit = (values) => {
    dispatch(StoreAgreementCreateAction(buildSavePayload(values), onSuccess, onError));
  };

  // Approve saves what is on screen first (remarks included), so it never has to
  // wait for a separate Save. Resolves false if the save fails.
  const saveBeforeApprove = (values) =>
    new Promise((resolve) => {
      const payload = buildSavePayload(values);
      dispatch(
        StoreAgreementCreateAction(
          payload,
          () => {
            // Merged, not replaced: the save response may leave fields out, and
            // the form reinitialises from this record.
            setStoreData((prev) => ({
              ...prev,
              agreement: { ...prev?.agreement, ...payload },
            }));
            resolve(true);
          },
          (data) => {
            errorToast(data?.data?.message ?? "Could not save before approving");
            resolve(false);
          }
        )
      );
    });

  const onSuccess = (data) => {
    setStoreData({
      ...storeData,
      agreement: data?.data,
    });
    successToast(data?.data?.message);
  };
  const onError = (data) => {
    errorToast(data?.data?.message);
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      // validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ values, setFieldValue, initialValues: savedValues }) => {
        // An approved agreement is final: the whole tab, Save and Decline
        // included, goes read-only as soon as Approve succeeds.
        const tabDisabled = disabled || !!values?.store_agreement_is_approve;
        return (
        <Form style={{ height: "calc(100% - 5rem)" }} className="pt-5">
          <div className="flex flex-col justify-between h-full">
            <div>
              <div className="w-full 2xl:w-[40%] xl:w-[50%] lg:w-[60%] px-5 xl:px-10">
                <InputField
                  isDisabled={tabDisabled}
                  type="text"
                  name="agreement_witness"
                  placeholder="Enter Agreement Witness"
                  label="Agreement Witness"
                  labelWidth="10.5rem"
                />
                <InputField
                  isDisabled={tabDisabled}
                  type="text"
                  name="store_agreement_remarks"
                  placeholder="Enter Remarks"
                  label="Remarks"
                  labelWidth="10.5rem"
                />
                <ImageField
                  isDisabled={tabDisabled}
                  label="Digital Signature"
                  labelWidth="10.5rem"
                  name="store_agreement_digital_sign"
                />
              </div>
              {agreementPdfUrl && (
                <div className="px-5 xl:px-10 mt-4">
                  <p className="text-sm font-medium text-gray-600 mb-2">
                    Agreement Document
                  </p>
                  <div style={{ width: "100%", height: "800px" }}>
                    <embed
                      src={agreementPdfUrl}
                      type="application/pdf"
                      width="100%"
                      height="100%"
                    />
                  </div>
                </div>
              )}
              {operationalManualUrl && (
                <div className="px-5 xl:px-10 mt-4">
                  <p className="text-sm font-medium text-gray-600 mb-2">
                    Operational Manual
                  </p>
                  <div style={{ width: "100%", height: "800px" }}>
                    <embed
                      src={operationalManualUrl}
                      type="application/pdf"
                      width="100%"
                      height="100%"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="sticky" style={{ bottom: "0" }}>
              <div
                style={{ height: "5rem" }}
                className="px-5 xl:px-10 bg-D0D5DD flex items-center"
              >
                <CheckBox items={agreementTerms} isDisabled={tabDisabled} />
              </div>
              <div className="flex items-center justify-between px-10 mb-5x bg-white py-4 invoice-bottom-container">
                {permissionFn("store_document_process")?.approver ? (
                  <ApproveDeclineActions
                    storeId={id}
                    approveKey="store_agreement_is_approve"
                    rejectKey="store_agreement_is_reject"
                    record={initialValue}
                    isApproved={!!values?.store_agreement_is_approve}
                    isRejected={!!values?.store_agreement_is_reject}
                    disabled={tabDisabled}
                    // Decline can be repeated until the agreement is approved.
                    alwaysAllowDecline={!values?.store_agreement_is_approve}
                    // Remarks are optional for Approve; only Decline asks for a
                    // reason (in its own panel). Approve saves what is on screen
                    // first, so no separate Save is needed.
                    beforeApprove={() => saveBeforeApprove(values)}
                    // No unsaved-edits check here: Approve saves first.
                    onDecided={async (data) => {
                      await setFieldValue(
                        "store_agreement_is_approve",
                        !!data?.store_agreement_is_approve
                      );
                      await setFieldValue(
                        "store_agreement_is_reject",
                        !!data?.store_agreement_is_reject
                      );
                    }}
                  />
                ) : (
                  <span />
                )}
                {!tabDisabled && (
                  <AddButton prefixIcon={false} text="Save" type="submit" />
                )}
              </div>
            </div>
          </div>
        </Form>
      );
      }}
    </Formik>
  );
};

export default StoreAgreementTab;

const agreementTerms = [
  {
    label: "I agree to the store agreement and the terms and conditions contained therein.",
    name: "store_agreement_with_davaindia_conditions",
    isRequired: false,
  },
];